import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import CustomBreadcrumbs from "../components/navigation/breadcrumbs/CustomBreadcrumbs";
import { OfflineStatusDot, OnlineStatusDot } from "../components/StatusDots";
import { useEffect, useState } from "react";
import MonitoringChartContainer from "../components/monitoring/MonitoringChartContainer";
import { requestWithAuthorization } from "../network/apiRequests";
import { useParams } from "react-router-dom";
import { RiDeleteBinLine } from "react-icons/ri";

export default function MonitoringDetailPage() {

    const [sensorContainer, setSensorContainer] = useState(<></>)
    const [plotContainer, setPlotContainer] = useState(<></>)
    const [isLoadingSensorDetails, setIsLoadingSensorDetails] = useState(false)
    const [sensorIds, setSensorIds] = useState<string[]>([])
    const [sensorDetails, setSensorDetails] = useState<any[]>([])
    const [monitoringDetails, setMonitoringDetails] = useState<any>({})

    // get monitoring ID from URL
    let { monitoringId } = useParams()

    async function fetchSensorIds({ monitoringId }: { monitoringId: string }) {
        try {
            const response = await requestWithAuthorization(
                process.env.REACT_APP_API_HOSTNAME + "/monitoring/" + monitoringId
            )
            if (response.status === 200) {
                setSensorIds(response.data.sensors)
                setMonitoringDetails(response.data)
            } else {
                console.log("Error fetching sensor IDs")
            }
        }
        catch (error) {
            window.location.href = "/monitoring"
            console.error(error)
        }
    }

    async function fetchSensorData({ sensorId }: { sensorId: string }) {
        try {
            const response = await requestWithAuthorization(
                process.env.REACT_APP_API_HOSTNAME + "/sensor/" + sensorId
            );
            if (response.status === 200) {
                const newSensorData = response.data;
                setSensorDetails((prevSensorData) => {
                    const index = prevSensorData.findIndex(sensor => sensor.id === newSensorData.id);
                    if (index !== -1) {
                        // Update existing sensor data
                        const updatedSensorData = [...prevSensorData];
                        updatedSensorData[index] = newSensorData;
                        return updatedSensorData;
                    } else {
                        // Append new sensor data
                        return [...prevSensorData, newSensorData];
                    }
                });
            } else {
                console.log("Error fetching sensor data")
            }
            
        }
        catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        if (monitoringId === undefined) {
            return
        }
        fetchSensorIds({ monitoringId })
    }, [monitoringId])

    useEffect(() => {
        if (sensorIds.length > 0) {
            setIsLoadingSensorDetails(true);
            Promise.all(sensorIds.map(sensorId => fetchSensorData({ sensorId })))
                .finally(() => setIsLoadingSensorDetails(false));
        }
    }, [sensorIds]);

    useEffect(() => {
        if (isLoadingSensorDetails) {
            setSensorContainer(<Container fluid className="p-4 justify-content-center text-center"><Row><Col><Spinner variant="light" /></Col></Row></Container>)
        } else {
            setSensorContainer(
                <Container fluid className="p-0">
                    <Row className="px-1">
                        {sensorDetails.map((sensor) => (
                            <Col key={sensor.id} xs={12} sm={6} md={4} lg={3} xl={3} className="p-1">
                                <Container className="session-tile p-4 d-flex" onClick={() => showPlotContainer(sensor.id, sensor.info.last_timestamp)} style={{cursor: "pointer"}}>
                                    <Row>
                                        <Col>
                                            <Row className="pb-0 d-flex align-items-center">
                                                <Col><span className="session-tile-title">{sensor.name}</span></Col>
                                                <Col className="d-flex justify-content-end text-end">
                                                    {sensor.info.status === "online" ? <OnlineStatusDot /> : <OfflineStatusDot />}
                                                </Col>
                                            </Row>
                                            <Row className="pt-0">
                                                <Col><span className="session-tile-subtitle">Last Recording: {sensor.info.last_recording_name}</span></Col>
                                            </Row>
                                            <Row className="pt-0">
                                                <Col><span className="session-tile-subtitle">Last Recording Timestamp: {sensor.info.last_timestamp}</span></Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Container>
                            </Col>
                        ))}
                    </Row>
                </Container>
            )
        }
    }, [isLoadingSensorDetails, sensorDetails])

    function showPlotContainer(sensorId: string, last_timestamp: string) {
        setPlotContainer(<MonitoringChartContainer sensorId={sensorId} last_timestamp={last_timestamp} />)
    }

    async function deleteMonitoring(monitoringId: string | undefined) {
        if (monitoringId === undefined) {
            return
        }
        try {
            const response = await requestWithAuthorization(
                process.env.REACT_APP_API_HOSTNAME + "/monitoring/" + monitoringId,
                {},
                "delete"
            )
            if (response.status === 204) {
                console.log("Monitoring deleted")
                window.location.href = "/monitoring"
            } else {
                console.log("Error deleting monitoring")
            }
        }
        catch (error) {
            console.error(error)
        }
    }

    return (
        <Container fluid className="background-color text-white p-4">
            <Row>
                <Col>
                    <CustomBreadcrumbs
                        path={[{ path: "/monitoring", title: "Monitoring" }]}
                        page={monitoringDetails.name}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                <p className="wind-form-label">BEWARE: We store the timestamps in UTC without timezone information. This means that depending on the timezone the recording was made in, it might be several hours offset. (e.g. recording in CEST have a 2 hour negative offset - 14:20 instead of 16:20)</p>
                </Col>
            </Row>
            <Container fluid className="p-0">
                <Row className="d-flex">
                    <Col>
                        <h1>{monitoringDetails.name}</h1>
                    </Col>
                    <Col className="justify-content-end text-end">
                        <Button className="wind-btn-outline btn-red" onClick={() => deleteMonitoring(monitoringId)} size="lg"><RiDeleteBinLine /> Delete Monitoring</Button>
                    </Col>
                </Row>
                <Row>
                    <Col><p className="wind-form-label">{monitoringDetails.description}</p></Col>
                </Row>
                {sensorContainer}
                <Container fluid className="p-0">
                    {plotContainer}
                </Container>
            </Container>
        </Container>
    )
}